import { create } from "zustand";
import { persist } from "zustand/middleware";
import configs from "../config";
import { trackEvent } from '../contexts/PostHogProvider';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      userPermissions: [],
      isAuthenticated: false,
      hasHydrated: false,
      setHasHydrated: (state) => set({ hasHydrated: state }),

      login: async (email, password, tenantSlugOverride = undefined) => {
        try {
          const { getTenantHeaders } = await import("../utils/apiClient");
          let tenantHeaders = {};
          if (tenantSlugOverride === null) {
            // Explicitly no tenant context (platform admin path)
            tenantHeaders = {};
          } else if (typeof tenantSlugOverride === 'string' && tenantSlugOverride.trim()) {
            // Use verified workspace slug directly for login
            tenantHeaders = { 'X-Tenant-Subdomain': tenantSlugOverride.trim().toLowerCase() };
          } else {
            // Backward-compatible fallback
            tenantHeaders = getTenantHeaders();
            delete tenantHeaders['Content-Type'];
          }

          const response = await fetch(
            `${configs.BACKEND_URL}/api/user/login`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...tenantHeaders,
              },
              credentials: "include",
              body: JSON.stringify({
                email,
                password,
              }),
            }
          );

          if (!response.ok) {
            let message = "Login failed";
            let code = null;
            try { const data = await response.json(); if (data?.error) message = data.error; if (data?.code) code = data.code; } catch {}
            const err = new Error(message);
            err.code = code;
            throw err;
          }

          const userData = await response.json();

          // Fetch user permissions
          let userPermissions = [];
          if (userData.role !== 'superuser' && userData.role !== 'owner') {
            try {
              const permissionsResponse = await fetch(
                `${configs.BACKEND_URL}/api/user/permissions`,
                {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                    ...tenantHeaders,
                  },
                  credentials: "include",
                }
              );
              if (permissionsResponse.ok) {
                const permissionsData = await permissionsResponse.json();
                userPermissions = permissionsData.permissions || [];
              } else {
                console.error("Failed to fetch permissions:", permissionsResponse.status, permissionsResponse.statusText);
              }
            } catch (error) {
              console.error("Error fetching permissions:", error);
            }
          } else {
            // Superuser and owner have all permissions
            userPermissions = ['*'];
          }

          set({
            user: userData,
            userPermissions,
            isAuthenticated: true,
          });
          return userData;
        } catch (error) {
          console.error("Login error:", error);
          throw error;
        }
      },

      logout: async () => {
        try {
          const { getTenantHeaders } = await import("../utils/apiClient");
          const response = await fetch(
            `${configs.BACKEND_URL}/api/user/logout`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...getTenantHeaders(),
              },
              credentials: "include",
            }
          );

          if (!response.ok) {
            throw new Error("Logout failed");
          }
        } catch (error) {
          throw error;
        }

        const { user } = get();
        trackEvent('user_logged_out', { role: user?.role });

        set({
          user: null,
          userPermissions: [],
          isAuthenticated: false,
        });
      },

      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : state.user,
        }));
      },

      hasPermission: (perm) => {
        const state = get();
        const { user, userPermissions } = state;
        
        if (!user) return false;
        
        // Superuser and owner have all permissions
        if (user.role === 'superuser' || user.role === 'owner') return true;
        
        // Check if user has the specific permission
        return userPermissions.includes(perm) || userPermissions.includes('*');
      },

      // Helper function to check multiple permissions (user needs ALL of them)
      hasAllPermissions: (permissions) => {
        const state = get();
        const { user, userPermissions } = state;
        
        if (!user) return false;
        if (user.role === 'superuser' || user.role === 'owner') return true;
        
        return permissions.every(perm => 
          userPermissions.includes(perm) || userPermissions.includes('*')
        );
      },

      // Helper function to check multiple permissions (user needs ANY of them)
      hasAnyPermission: (permissions) => {
        const state = get();
        const { user, userPermissions } = state;
        
        if (!user) return false;
        if (user.role === 'superuser' || user.role === 'owner') return true;
        
        return permissions.some(perm => 
          userPermissions.includes(perm) || userPermissions.includes('*')
        );
      },
    }),
    {
      name: "auth-storage",
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

export default useAuthStore;
